<template>
  <div class="kiosk">
    <header class="kiosk__header">
      <img class="kiosk__emblem" src="@/assets/images/knb_logo.png" alt="ПС" />
      <div class="kiosk__title">
        <div class="kiosk__t1">{{ $t('mpo.kiosk.surveyTitle') }}</div>
        <div v-if="state" class="kiosk__t2">{{ state.question_set.name }}</div>
      </div>
      <div class="kiosk__counter" v-if="state">
        {{ $t('mpo.kiosk.passedOfTotal', { passed: state.passed_count, total: state.total }) }}
      </div>
    </header>

    <div v-if="loading" class="kiosk__loader">{{ $t('mpo.kiosk.loading') }}</div>

    <div v-else-if="loadError" class="kiosk__error">
      <div class="kiosk__error-title">{{ $t('mpo.kiosk.openError') }}</div>
      <div class="kiosk__error-desc">{{ loadError }}</div>
    </div>

    <!-- Экран 1. Выбор себя из списка -->
    <div v-else-if="stage === 'pick'" class="kiosk__stage">
      <div class="pick">
        <h2 class="pick__title">{{ $t('mpo.kiosk.pickYourself') }}</h2>
        <p class="pick__hint">{{ $t('mpo.kiosk.pickHint') }}</p>

        <div v-if="state.remaining.length === 0" class="pick__done">
          <div class="pick__done-emoji">✅</div>
          <div class="pick__done-title">{{ $t('mpo.kiosk.allDone') }}</div>
          <div class="pick__done-desc">{{ $t('mpo.kiosk.allDoneHint') }}</div>
        </div>

        <div v-else class="pick__grid">
          <button
            v-for="p in state.remaining"
            :key="p.id"
            type="button"
            class="pick__btn"
            @click="pickSelf(p)"
          >
            <span class="pick__num">{{ p.personal_number || '—' }}</span>
            <span class="pick__name">{{ p.full_name }}</span>
          </button>
        </div>

        <details v-if="state.passed_count" class="pick__passed">
          <summary>{{ $t('mpo.kiosk.alreadyPassed', { n: state.passed_count }) }}</summary>
          <ul>
            <li v-for="pid in state.passed_ids" :key="pid">{{ nameById(pid) || pid }}</li>
          </ul>
        </details>
      </div>
    </div>

    <!-- Экран 2. Прохождение опроса -->
    <div v-else-if="stage === 'quiz'" class="kiosk__stage">
      <div class="quiz">
        <div class="quiz__who">{{ $t('mpo.kiosk.answering') }} <b>{{ me.full_name }}</b></div>

        <div class="quiz__q">
          <div class="quiz__q-index">{{ $t('mpo.kiosk.questionOf', { n: currentIdx + 1, total: questions.length }) }}</div>
          <div class="quiz__q-text">{{ currentQuestion.text }}</div>
          <div class="quiz__q-hint">
            {{ $t('mpo.kiosk.pickLimit', { n: limit }) }}
            <span v-if="isOptional">{{ $t('mpo.kiosk.optional') }}</span>
          </div>
        </div>

        <div class="quiz__list">
          <label
            v-for="p in otherParticipants"
            :key="p.id"
            class="quiz__opt"
            :class="{ 'is-checked': isPicked(p.id) }"
          >
            <input
              type="checkbox"
              :checked="isPicked(p.id)"
              :disabled="!isPicked(p.id) && pickedForCurrent.length >= limit"
              @change="togglePick(p)"
            />
            <span class="quiz__opt-num">{{ p.personal_number || '—' }}</span>
            <span class="quiz__opt-name">{{ p.full_name }}</span>
          </label>
        </div>

        <div class="quiz__footer">
          <button class="btn btn--secondary" :disabled="currentIdx === 0" @click="prevQuestion">{{ $t('mpo.kiosk.back') }}</button>
          <div class="quiz__spacer" />
          <button
            v-if="currentIdx < questions.length - 1"
            class="btn btn--primary"
            :disabled="!canNext"
            @click="nextQuestion"
          >{{ $t('mpo.kiosk.next') }}</button>
          <button
            v-else
            class="btn btn--primary"
            :disabled="!canNext || submitting"
            @click="submit"
          >{{ submitting ? $t('mpo.kiosk.sending') : $t('mpo.kiosk.finish') }}</button>
        </div>

        <div v-if="submitError" class="quiz__err">{{ submitError }}</div>
      </div>
    </div>

    <!-- Экран 3. Спасибо -->
    <div v-else-if="stage === 'thanks'" class="kiosk__stage">
      <div class="thanks">
        <div class="thanks__emoji">🎯</div>
        <div class="thanks__title">{{ $t('mpo.kiosk.thanks') }}</div>
        <div class="thanks__desc">{{ $t('mpo.kiosk.thanksHint') }}</div>
        <button class="btn btn--primary" @click="backToPick">{{ $t('mpo.kiosk.continue') }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import { getKioskState, submitKioskChoices } from '@/api/mpo'

export default {
  name: 'KioskSurveyView',
  data() {
    return {
      token: this.$route.params.token,
      loading: true,
      loadError: null,
      state: null,
      // stage: 'pick' | 'quiz' | 'thanks'
      stage: 'pick',
      me: null,
      currentIdx: 0,
      // answers: { [questionId]: Set<userId> }
      answers: {},
      submitting: false,
      submitError: null,
    }
  },
  computed: {
    questions() {
      return this.state?.question_set?.questions || []
    },
    limit() {
      return this.state?.question_set?.limit_choices || 3
    },
    isOptional() {
      return !!this.state?.question_set?.is_optional
    },
    currentQuestion() {
      return this.questions[this.currentIdx] || { id: null, text: '' }
    },
    otherParticipants() {
      if (!this.state || !this.me) return []
      return (this.state.participants || []).filter(p => p.id !== this.me.id)
    },
    pickedForCurrent() {
      const qid = this.currentQuestion.id
      const set = this.answers[qid]
      return set ? Array.from(set) : []
    },
    canNext() {
      // либо выбрано >=1, либо вопрос-опциональный
      return this.pickedForCurrent.length > 0 || this.isOptional
    },
  },
  async mounted() {
    await this.loadState()
    // авто-обновление счётчика раз в 30 сек, если сейчас на экране выбора
    this.__poll = setInterval(() => {
      if (this.stage === 'pick') this.loadState({ silent: true })
    }, 30000)
  },
  beforeUnmount() {
    clearInterval(this.__poll)
  },
  methods: {
    async loadState({ silent = false } = {}) {
      if (!silent) this.loading = true
      try {
        this.state = await getKioskState(this.token)
        this.loadError = null
      } catch (e) {
        this.loadError = this.$t('mpo.kiosk.notFound')
        console.error(e)
      } finally {
        if (!silent) this.loading = false
      }
    },
    pickSelf(p) {
      this.me = p
      this.currentIdx = 0
      this.answers = {}
      for (const q of this.questions) {
        this.answers[q.id] = new Set()
      }
      this.stage = 'quiz'
    },
    nameById(id) {
      const p = (this.state?.participants || []).find(x => String(x.id) === String(id))
      return p ? p.full_name : ''
    },
    isPicked(userId) {
      const qid = this.currentQuestion.id
      const set = this.answers[qid]
      return set ? set.has(userId) : false
    },
    togglePick(p) {
      const qid = this.currentQuestion.id
      const set = this.answers[qid] || new Set()
      if (set.has(p.id)) set.delete(p.id)
      else if (set.size < this.limit) set.add(p.id)
      this.answers = { ...this.answers, [qid]: set }
    },
    prevQuestion() {
      if (this.currentIdx > 0) this.currentIdx--
    },
    nextQuestion() {
      if (this.currentIdx < this.questions.length - 1) this.currentIdx++
    },
    buildChoicesPayload() {
      // на выходе: [{ question_id, chosen_user_id, choice_type, order }]
      const out = []
      for (const q of this.questions) {
        const set = this.answers[q.id]
        if (!set || !set.size) continue
        const order = []
        set.forEach(uid => order.push(uid))
        const type = String(q.choice_type || '').toLowerCase()
        const choiceType = type === '-' || type === 'minus' ? 'minus' : 'plus'
        order.forEach((uid, idx) => {
          out.push({
            question_id: q.id,
            chosen_user_id: uid,
            choice_type: choiceType,
            order: idx + 1,
          })
        })
      }
      return out
    },
    async submit() {
      if (!this.me) return
      this.submitError = null
      this.submitting = true
      try {
        const choices = this.buildChoicesPayload()
        await submitKioskChoices(this.token, this.me.id, choices)
        this.stage = 'thanks'
      } catch (e) {
        this.submitError = e?.payload?.error || e.message || this.$t('mpo.kiosk.submitError')
      } finally {
        this.submitting = false
      }
    },
    async backToPick() {
      this.me = null
      this.answers = {}
      this.currentIdx = 0
      this.stage = 'pick'
      await this.loadState()
    },
  },
}
</script>

<style scoped>
.kiosk {
  min-height: 100vh;
  background: #F3F4F6;
  padding: 24px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.kiosk__header {
  display: flex;
  align-items: center;
  gap: 16px;
  background: #fff;
  padding: 16px 20px;
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}
.kiosk__emblem { width: 56px; height: 56px; object-fit: contain; }
.kiosk__title { flex: 1 1 auto; }
.kiosk__t1 { font-size: 20px; font-weight: 800; color: #111827; }
.kiosk__t2 { font-size: 14px; color: #6B7280; margin-top: 2px; }
.kiosk__counter { font-size: 15px; color: #374151; padding: 8px 14px; background: #F3F4F6; border-radius: 8px; }
.kiosk__loader, .kiosk__error { padding: 40px; text-align: center; color: #6B7280; background: #fff; border-radius: 12px; }
.kiosk__error-title { font-size: 20px; font-weight: 700; color: #DC2626; margin-bottom: 8px; }

.kiosk__stage { flex: 1 1 auto; display: flex; align-items: flex-start; justify-content: center; }

/* ---- Pick self ---- */
.pick { width: 100%; max-width: 1000px; background: #fff; border-radius: 14px; padding: 28px; }
.pick__title { margin: 0 0 6px; font-size: 24px; font-weight: 800; color: #111827; }
.pick__hint { margin: 0 0 20px; color: #6B7280; }
.pick__grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 12px; }
.pick__btn {
  display: flex; align-items: center; gap: 12px; padding: 16px; border: 1px solid #E5E7EB; border-radius: 12px;
  background: #fff; cursor: pointer; text-align: left; font: inherit; transition: border-color 0.15s, transform 0.1s;
}
.pick__btn:hover { border-color: #2F78EE; }
.pick__btn:active { transform: scale(0.99); }
.pick__num { flex: 0 0 auto; min-width: 48px; padding: 4px 8px; background: #F3F4F6; border-radius: 6px; text-align: center; font-weight: 700; color: #374151; }
.pick__name { flex: 1 1 auto; color: #111827; font-weight: 600; }

.pick__passed { margin-top: 24px; color: #6B7280; }
.pick__passed summary { cursor: pointer; padding: 4px 0; }
.pick__passed ul { margin: 8px 0 0; padding-left: 20px; }

.pick__done { padding: 40px; text-align: center; background: #ECFDF5; border-radius: 12px; }
.pick__done-emoji { font-size: 64px; }
.pick__done-title { font-size: 24px; font-weight: 800; color: #065F46; margin-top: 8px; }
.pick__done-desc { color: #047857; margin-top: 4px; }

/* ---- Quiz ---- */
.quiz { width: 100%; max-width: 900px; background: #fff; border-radius: 14px; padding: 28px; }
.quiz__who { padding: 8px 12px; background: #EFF6FF; color: #1E40AF; border-radius: 8px; font-size: 14px; margin-bottom: 20px; display: inline-block; }
.quiz__q-index { color: #6B7280; font-size: 13px; text-transform: uppercase; letter-spacing: 0.5px; }
.quiz__q-text { margin: 6px 0 4px; font-size: 22px; font-weight: 700; color: #111827; line-height: 1.35; }
.quiz__q-hint { color: #6B7280; font-size: 14px; margin-bottom: 20px; }
.quiz__list { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 8px; margin-bottom: 24px; }
.quiz__opt { display: flex; align-items: center; gap: 10px; padding: 12px; border: 1px solid #E5E7EB; border-radius: 10px; cursor: pointer; transition: border-color 0.15s, background 0.15s; }
.quiz__opt:hover { border-color: #A5B4FC; background: #F5F3FF; }
.quiz__opt.is-checked { border-color: #2F78EE; background: #EFF6FF; }
.quiz__opt input { width: 18px; height: 18px; cursor: pointer; accent-color: #2F78EE; }
.quiz__opt input:disabled { cursor: not-allowed; }
.quiz__opt-num { min-width: 44px; text-align: center; padding: 2px 6px; background: #F3F4F6; border-radius: 4px; font-weight: 700; color: #374151; font-size: 13px; }
.quiz__opt-name { flex: 1 1 auto; color: #111827; }

.quiz__footer { display: flex; align-items: center; gap: 12px; }
.quiz__spacer { flex: 1 1 auto; }
.quiz__err { margin-top: 12px; padding: 10px 14px; border: 1px solid #FCA5A5; background: #FEF2F2; color: #991B1B; border-radius: 8px; }

/* ---- Thanks ---- */
.thanks { width: 100%; max-width: 640px; background: #fff; border-radius: 14px; padding: 48px; text-align: center; }
.thanks__emoji { font-size: 72px; }
.thanks__title { margin-top: 12px; font-size: 26px; font-weight: 800; color: #111827; }
.thanks__desc { margin-top: 6px; color: #6B7280; font-size: 15px; }
.thanks .btn { margin-top: 24px; }

/* ---- Buttons ---- */
.btn { padding: 10px 20px; border-radius: 10px; border: 0; cursor: pointer; font-size: 15px; font-weight: 600; font-family: inherit; }
.btn:disabled { opacity: 0.5; cursor: not-allowed; }
.btn--primary { background: #2F78EE; color: #fff; }
.btn--primary:hover:not(:disabled) { background: #1E5BB8; }
.btn--secondary { background: #F3F4F6; color: #374151; }
.btn--secondary:hover:not(:disabled) { background: #E5E7EB; }
</style>
